import { useMemo, useState } from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { CHART_COLORS, currentCalendarYear, formatChartTooltipValue, parseChartYearFilter } from '../../chartUtils';
import { useApplicationStatusDistributionQuery } from '../../hooks/useDashboardAnalytics';
import { ChartCard } from './ChartCard';
import { ChartYearFilter } from './ChartYearFilter';

type ApplicationStatusDistributionChartProps = {
  regionalCode: string;
};

const STATUS_CONFIG = [
  { key: 'pending' as const, label: 'Pending', color: CHART_COLORS.pending },
  { key: 'assigned' as const, label: 'Under Review', color: CHART_COLORS.underReview },
  { key: 'approved' as const, label: 'Approved', color: CHART_COLORS.approved },
  { key: 'reject' as const, label: 'Rejected', color: CHART_COLORS.rejected },
];

export function ApplicationStatusDistributionChart({
  regionalCode,
}: ApplicationStatusDistributionChartProps) {
  const [yearFilter, setYearFilter] = useState(String(currentCalendarYear()));
  const year = parseChartYearFilter(yearFilter) ?? currentCalendarYear();
  const { data, isLoading, errorMessage } = useApplicationStatusDistributionQuery(
    regionalCode,
    year
  );

  const chartData = useMemo(() => {
    if (!data) return [];
    return STATUS_CONFIG.map((item) => ({
      name: item.label,
      value: data[item.key],
      color: item.color,
    })).filter((item) => item.value > 0);
  }, [data]);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <ChartCard
      title="Application Status Distribution"
      subtitle={`National total · ${total.toLocaleString()} applications`}
      toolbar={<ChartYearFilter value={yearFilter} onChange={setYearFilter} labelId="app-status-year" />}
      loading={isLoading}
      error={errorMessage}
    >
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="45%"
              outerRadius={95}
              label={({ name, value }) => `${name}: ${value}`}
              labelLine
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => formatChartTooltipValue(value)} />
            <Legend verticalAlign="bottom" />
          </PieChart>
        </ResponsiveContainer>
      ) : null}
    </ChartCard>
  );
}
